"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Zap, Loader2 } from "lucide-react";
import { Pricing as PricingType } from "@/types/blocks/pricing";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

export default function Pricing({ pricing }: { pricing: PricingType }) {
  const { data: session } = useSession();
  const router = useRouter();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  if (!pricing || !pricing.items) return null;

  const handleBuy = async (packId: string) => {
    if (!session) {
      window.dispatchEvent(new CustomEvent("open-sign-modal"));
      return;
    }

    setLoadingId(packId);
    try {
      const res = await fetch("/api/checkout/creem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packId }),
      });

      const data = await res.json();

      if (!res.ok || !data.url) {
        console.error("[Pricing] Checkout error:", data.error);
        alert(data.error || "Failed to start checkout. Please try again.");
        return;
      }

      window.location.href = data.url;
    } catch (e) {
      console.error("[Pricing] Network error:", e);
      alert("Network error. Please try again.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section
      id="pricing"
      className="relative z-0 pb-14 md:pb-20 lg:pb-24"
      style={{ paddingTop: 'calc(96px + var(--locale-banner-height, 0px))' }}
    >
      <div className="mx-auto mb-8 px-4 text-center md:mb-10 md:px-8">
        <h1 className="sr-only">ProductVisuals Pricing</h1>
        <h2 className="mb-4 text-3xl font-bold text-pretty lg:text-4xl">
          {pricing.title || "Simple, one-time credit packs"}
        </h2>
        <p className="text-muted-foreground mx-auto max-w-xl lg:text-lg">
          {pricing.subtitle || "Credits never expire · No subscription · 1 credit = 1 image generation"}
        </p>
      </div>

      <div className="container">
        <div className="relative z-0 grid w-full gap-4 md:gap-6 lg:gap-8 md:grid-cols-3">
          {pricing.items.map((item: any, index: number) => {
            const isFeatured = item.is_featured || item.featured;
            const isLoading = loadingId === item.id;

            return (
              <Card
                key={index}
                data-slot="card"
                className={`text-card-foreground flex flex-col gap-6 rounded-xl py-6 relative overflow-visible border transition-all duration-300 ${
                  isFeatured
                    ? "border-primary/40 shadow-[0_8px_32px_rgba(0,0,0,0.12)] md:-translate-y-1 md:scale-[1.04]"
                    : "border-border/70 bg-card/80 hover:-translate-y-0.5"
                }`}
              >
                {isFeatured && (
                  <span className="absolute inset-x-0 -top-3 mx-auto flex h-7 w-fit items-center rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                    {item.badge_icon || "Most Popular"}
                  </span>
                )}

                <div
                  data-slot="card-header"
                  className={`px-6 pb-0 ${isFeatured ? "pt-5" : "pt-2"}`}
                >
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>

                  <div className="my-4 flex items-baseline gap-1">
                    <span className="text-4xl font-bold">${item.price}</span>
                    <span className="text-sm text-muted-foreground">one-time</span>
                  </div>

                  <div className="mb-2 text-xs text-muted-foreground font-medium">
                    {item.credits} credits · ${(Number(item.price) / item.credits).toFixed(2)} per generation
                  </div>

                  <Button
                    onClick={() => handleBuy(item.id)}
                    disabled={isLoading || loadingId !== null}
                    className={`mt-2 w-full ${isFeatured ? "bg-primary hover:bg-primary/90" : ""}`}
                    variant={isFeatured ? "default" : "outline"}
                  >
                    {isLoading ? (
                      <Loader2 className="size-4 mr-1 animate-spin" />
                    ) : (
                      <Zap className="size-4 mr-1" />
                    )}
                    {isLoading ? "Redirecting..." : (item.button_text || "Buy now")}
                  </Button>
                </div>

                <div data-slot="card-content" className="px-6 space-y-3">
                  <hr className="border-dashed" />
                  <ul className="space-y-2 text-sm">
                    {item.features?.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Check className="size-3.5 shrink-0 text-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Refunds available for unused credits within 30 days · See our{" "}
          <a href="/refund-policy" className="underline underline-offset-2">Refund Policy</a>
        </p>
      </div>
    </section>
  );
}
